import { useEntityNotes } from '../../components/entity-notes/useEntityNotes';
import { inNotesCell, notesGridSx } from '../../components/entity-notes/notesColumn';
import { useState, useMemo, useRef } from 'react';
import {
  Box,
} from '@mui/material';
import { Add, Edit, ContentCopy, Download, Visibility, VisibilityOff } from '@mui/icons-material';
import TooltipIconButton from '../../components/TooltipIconButton';
import { DataGridPremium, GridRenderCellParams, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import type { GridSortModel } from '@mui/x-data-grid-premium';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { offersService, Offer } from '../../services/offers.service';
import { downloadCsv } from '../../utils/csv-export';
import { useModalState } from '../../hooks/useModalState';
import { useSnackbar } from 'notistack';
import EntityGridToolbar from '../../components/EntityGridToolbar';
import TagSelect from '../../components/TagSelect';
import EntityFilterSelect from '../../components/EntityFilterSelect';
import { EntityOption } from '../../components/entity-fetchers';
import BulkEditDialog, { BulkFieldDef } from '../../components/BulkEditDialog';
import { resolveSelectedIds, emptySelection, buildEntityWhere, applyBulkUpdate, BulkResult } from '../../utils/entity-list';
import { useServerPagination } from '../../hooks/useServerPagination';
import { useGridHeight } from '../../hooks/useGridHeight';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { useDebounced } from '../../hooks/useDebounced';
import { activityOrderBy } from '../../components/ActivityCell';
import { offersColumns } from './offers.columns';

const BULK_FIELDS: BulkFieldDef[] = [
  { key: 'tag_ids', label: 'Tags', type: 'tags' },
  { key: 'status', label: 'Status', type: 'status' },
  { key: 'traffic_cap_limit', label: 'Traffic cap', type: 'number' },
];

const EDITABLE_FIELDS = ['traffic_cap_limit'];

export default function OffersListTab() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { setModal } = useModalState('offer');
  const [selection, setSelection] = useState<GridRowSelectionModel>(emptySelection);
  const [statuses, setStatuses] = useUrlState<string[]>('status', ['active'], codecs.statusCsv);
  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);
  const [search, setSearch] = useUrlState('q', '', codecs.str);
  const [filterTagIds, setFilterTagIds] = useUrlState<string[]>('tags', [], codecs.json<string[]>());
  const [networkSel, setNetworkSel] = useUrlState<EntityOption[]>('network', [], codecs.json<EntityOption[]>());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const debouncedSearch = useDebounced(search);
  const where = useMemo(
    () => buildEntityWhere('offer', { search: debouncedSearch, statuses, tagIds: filterTagIds }, ['name', 'url'],
      networkSel.length ? [{ offer: { network_id: { op: 'in', value: networkSel.map((o) => o.id) } } }] : []),
    [debouncedSearch, statuses, filterTagIds, networkSel],
  );
  const { gridProps, offset, limit } = useServerPagination(50, where);
  const [sort, setSort] = useState<GridSortModel>([{ field: '_activity', sort: 'desc' }]);
  const orderBy = activityOrderBy(sort);


  const { data, isFetching } = useQuery({
    queryKey: ['offers', where, offset, limit, sort],
    queryFn: () => offersService.query({ where, offset, limit, ...(orderBy && { order_by: orderBy, order: sort[0]?.sort === 'asc' ? 'asc' as const : 'desc' as const }) }),
    placeholderData: (prev) => prev,
  });

  const rows = useMemo(() => data?.data || [], [data]);

  const selectedIds = useMemo(() => resolveSelectedIds(selection, rows), [selection, rows]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['offers'] });
  const notes = useEntityNotes('offer');
  const bulkAction = async (fn: () => Promise<unknown>, okMsg: string) => {
    try { await fn(); invalidate(); setSelection(emptySelection); enqueueSnackbar(okMsg, { variant: 'success' }); }
    catch (e) { enqueueSnackbar(e instanceof Error ? e.message : 'Action failed', { variant: 'error' }); }
  };
  const archiveSelected = () => bulkAction(() => Promise.all(selectedIds.map((id) => offersService.delete(id))), `Archived ${selectedIds.length}`);
  const unarchiveSelected = () => bulkAction(() => Promise.all(selectedIds.map((id) => offersService.unarchive(id))), `Unarchived ${selectedIds.length}`);
  const runBulk = async (result: BulkResult) => {
    setBulkLoading(true);
    try {
      await applyBulkUpdate((id, patch) => offersService.update(id, patch), selectedIds, rows, result);
      invalidate(); setSelection(emptySelection); setBulkOpen(false);
      enqueueSnackbar(`Updated ${selectedIds.length} ${selectedIds.length === 1 ? 'item' : 'items'}`, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Bulk update failed', { variant: 'error' });
    } finally { setBulkLoading(false); }
  };

  const processRowUpdate = async (newRow: Offer, oldRow: Offer): Promise<Offer> => {
    newRow = await notes.saveIfChanged(newRow, oldRow);
    const patch: Partial<Offer> = {};
    if (newRow.traffic_cap_limit !== oldRow.traffic_cap_limit) {
      const cap = Number(newRow.traffic_cap_limit);
      if (!Number.isFinite(cap) || cap < 0) throw new Error('Traffic cap must be 0 or greater');
      patch.traffic_cap_limit = cap;
    }
    if (Object.keys(patch).length === 0) return newRow;
    await offersService.update(oldRow.id, patch);
    invalidate();
    enqueueSnackbar('Updated', { variant: 'success' });
    return { ...newRow, ...patch };
  };
  const onProcessRowUpdateError = (e: unknown) => enqueueSnackbar(e instanceof Error ? e.message : 'Update failed', { variant: 'error' });

  const handleExportCsv = () => {
    const rows = data?.data || [];
    const cols = ['name', 'url', 'currency', 'traffic_cap_limit', 'status', 'created_at'];
    downloadCsv(rows, cols, 'offers-export.csv');
  };

  const columns = offersColumns({
    notes: notes.column,
    includeNetwork: true,
    actions: {
      field: 'actions', headerName: '', width: 70, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0, alignItems: 'center', height: '100%' }}>
          <TooltipIconButton size="small" title="Edit" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Update' })}>
            <Edit sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          <TooltipIconButton size="small" title="Duplicate" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Duplicate' })}>
            <ContentCopy sx={{ fontSize: 16 }} />
          </TooltipIconButton>
        </Box>
      ),
    },
  });

  return (
    <Box>
      {/* Empty on purpose: useGridHeight measures the grid's top from here. */}
      <Box ref={filterRef} />

      <DataGridPremium
        rows={rows}
        columns={columns}
        loading={isFetching}
        {...gridProps(data?.total)}
        sortingMode="server"
        sortModel={sort}
        onSortModelChange={setSort}
        showToolbar
        slots={{ toolbar: EntityGridToolbar }}
        slotProps={{ toolbar: {
          search, onSearch: setSearch,
          filters: (
            <>
              <EntityFilterSelect field="network_id" value={networkSel} onChange={setNetworkSel} label="Network" sx={{ minWidth: 180, maxWidth: 280 }} />
              <TagSelect value={filterTagIds} onChange={setFilterTagIds} label="Filter by tags" allowCreate={false} sx={{ minWidth: 200 }} />
            </>
          ),
          statuses: { value: statuses, onChange: setStatuses },
          actions: [
            { icon: Download, hint: 'Export CSV', onClick: handleExportCsv, disabled: !data?.data?.length },
            { icon: Add, hint: 'Create offer', onClick: () => setModal({ open: true, reason: 'Create' }) },
          ],
          selectedCount: selectedIds.length,
          pluralName: 'offers',
          selectionActions: [
            { icon: Edit, hint: 'Bulk edit', onClick: () => setBulkOpen(true), color: 'inherit' as const },
            { icon: VisibilityOff, hint: 'Archive', onClick: archiveSelected, color: 'inherit' as const },
            { icon: Visibility, hint: 'Unarchive', onClick: unarchiveSelected, color: 'inherit' as const },
          ],
        } }}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selection}
        onRowSelectionModelChange={setSelection}
        keepNonExistentRowsSelected
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={onProcessRowUpdateError}
        onCellEditStart={notes.onCellEditStart}
        disableClipboardPaste
        onRowDoubleClick={(params: GridRowParams, event: React.MouseEvent) => {
          const target = event.target as HTMLElement;
          if (target.closest('[data-field="actions"]')) return;
          if (EDITABLE_FIELDS.some((f) => target.closest(`[data-field="${f}"]`))) return;
          if (notes.editable && inNotesCell(target)) return;
          setModal({ open: true, id: params.row.id, reason: 'Update' });
        }}
        sx={{ ...notesGridSx, bgcolor: 'background.paper', borderRadius: 2, height: gridHeight }}
      />

      <BulkEditDialog
        open={bulkOpen}
        count={selectedIds.length}
        fields={BULK_FIELDS}
        loading={bulkLoading}
        onClose={() => setBulkOpen(false)}
        onApply={runBulk}
      />
    </Box>
  );
}
